<script lang="ts" setup>
import type { AsyncDataRequestStatus } from '#app'
import ProductCard from '~/components/cards/ProductCard.vue'
import BasicButton from '~/components/form-elements/BasicButton.vue'
import SearchInput from '~/components/form-elements/SearchInput.vue'
import MindboxRecommendationsSection from '~/components/sections/MindboxRecommendationsSection.vue'
import { useWindowHelpers } from '~/composables/window-helpers'
import MindboxOperation from '~/enums/mindbox-operation'
import PageEnum from '~/enums/page'
import helpers from '~/helpers'
import JsonApiHelper from '~/helpers/json-api-helper'
import { useAuthStore } from '~/store/auth'
import { useProductsStore } from '~/store/products'
import type { BreadcrumbsItem } from '~/types/breadcrumbs'
import type { Page } from '~/types/json-api'
import type { ProductData } from '~/types/products'

const { $apiHelper, $toast } = useNuxtApp()
const router = useRouter()
const authStore = useAuthStore()
const productsStore = useProductsStore()
const { scrollToSavedPosition } = useWindowHelpers()

const breadcrumbsItems: BreadcrumbsItem[] = [{ name: 'Главная', path: PageEnum.Main }]
const messages = { failedToGetProducts: 'Не удалось получить товары.' }
const title = 'Избранное'

// Data.
const debounce = ref<any>(null)
const isDataLoading = ref(true)
const productsData = ref<ProductData[]>([])

// Filter.
const filter = ref({ query: null })

// Pagination.
const page = ref<Page>({ number: 1, size: 30 })
const totalPages = ref<number | null>(null)
const totalProducts = ref<number | null>(null)

const { status } = await useLazyAsyncData(loadData)

useHead({ title: `${title} — iTAB` })

const showSearchInput = computed(() => filter.value.query || filter.value.query === '' || productsData.value.length)

watch(
  () => filter.value.query,
  (): void => {
    if (debounce.value) {
      clearTimeout(debounce.value)
    }

    debounce.value = setTimeout(
      async () => {
        page.value.number = 1

        await loadData()
      },
      500
    )
  }
)
watch(status, (x: AsyncDataRequestStatus): void => {
  if (x === 'success') {
    scrollToSavedPosition()
  }
})

async function changePage (pageNumber: number): Promise<void> {
  window.scrollTo(0, 0)

  page.value.number = pageNumber

  await loadData()
}

async function goToAccountListsPage (): Promise<void> {
  await router.push(PageEnum.AccountLists)
}

async function loadData (): Promise<void> {
  isDataLoading.value = true

  productsData.value = []
  totalPages.value = null

  const { query } = filter.value
  const { number, size } = page.value
  const params = helpers.getParams([
    { name: 'filter[query]', value: query, condition: query },
    { name: 'page[number]', value: number, condition: true },
    { name: 'page[size]', value: size, condition: true }
  ])

  try {
    const response = authStore.isUser || authStore.isGuest
      ? await $apiHelper.wishListProducts.getWishListProducts(params)
      : { data: [], meta: { totalProducts: 0 } }

    if (!query || query === filter.value.query) {
      const { data, meta } = JsonApiHelper.denormalizeResponse(response as any) as any
      productsData.value = data
      totalProducts.value = meta.totalProducts
      totalPages.value = helpers.getTotalPages(meta.totalProducts, size)

      productsStore.setWishListTotalItems(meta.totalProducts)

      isDataLoading.value = false
    }
  } catch (error) {
    console.error(error)

    $toast.error(messages.failedToGetProducts)
  }
}
</script>

<template>
  <div>
    <section>
      <div class="container">
        <Breadcrumbs :current="title" :items="breadcrumbsItems" />

        <Heading :badge-text="totalProducts" :title="title">
          <template #buttons>
            <BasicButton
              v-if="authStore.isUser"
              color="white"
              is-small
              title="Списки"
              @click="goToAccountListsPage"
            />
          </template>
        </Heading>

        <SearchInput v-show="showSearchInput" v-model="filter.query" class="mb-6" placeholder="Поиск по «Избранному»" />

        <Preloader v-if="isDataLoading" />
        <div v-else-if="productsData.length" class="-m-1 flex flex-wrap md:-m-2">
          <div v-for="x in productsData" :key="x.id" class="w-1/2 p-1 md:w-1/3 md:p-2 xl:w-1/5">
            <ProductCard :product-data="x" />
          </div>
        </div>
        <div v-else class="content-wrapper">
          <Notification status="info">
            <template v-if="filter.query">
              Товары не&nbsp;найдены.
            </template>
            <template v-else>
              Нет товаров. Добавьте товар <NuxtLink :to="PageEnum.Search">
                <span>из&nbsp;каталога</span>
              </NuxtLink> в&nbsp;«Избранное», нажав кнопку <span class="whitespace-nowrap">«<SvgIcon name="outlined/favorite-border" />»</span>.
            </template>
          </Notification>
        </div>

        <Pagination
          :current-page="page.number"
          :is-on-white-background="false"
          :last-page="totalPages"
          @change-page="changePage"
        />
      </div>
    </section>

    <MindboxRecommendationsSection
      v-if="productsData.length"
      class="mt-10"
      :data="{ recommendation: { limit: 12 } }"
      :operation="MindboxOperation.RelatedProductsForWishList"
      :title="productsData.length === 1 ? 'С\xA0этим товаром покупают' : 'С\xA0этими товарами покупают'"
    />
  </div>
</template>
